import React, { lazy } from "react";
import { BC_HQ } from "app_constants";
import { ServersIcon } from "components/icons/ServersIcon";
import GroupManagementContentToolbar from "group_management/components/GroupManagementContentToolbar";
import { DateRangeSupport } from "helpers/shared/formatters/dates";
import { LD_FEATURE_FLAGS } from "types/context";
import type { ISecondaryNavProps, ISectionProps } from "types/navigation";

// Group Management: the full-page builder for custom location groups. It began
// as a "Manage groups" modal on the location picker and was promoted to its own
// page once groups gained create/edit/delete.
// It is a merchant-group-wide surface reached at /g/all/group-management (see
// validateOrCorrectLocationLevel, which forces group-management navigation to
// the whole-merchant-group scope). Gated by the group-management LaunchDarkly
// flag; writes are authorized server-side by can_manage_groups?.
//
// One component drives three URL-addressable modes so every view is
// bookmarkable and nothing is carried between clicks: the list at
// /group-management, create at /group-management/create, and edit at
// /group-management/edit/<slug> (slug = the :tab param). create/edit are
// deep-link only (hideInNav); the section itself is the list.
const groupManagementPage = lazy(
  () => import("containers/location_groups/group_management_page")
);

// Group admins: grant / revoke the merchant-group-wide group-admin role.
// A sibling of the group builder — both administer "who and what may act across
// this merchant group". SUPER-USER ONLY (requiresSuperAdmin below); hq_service
// enforces that independently, so the nav gate is UX rather than the control.
const groupAdminsPage = lazy(
  () => import("containers/location_groups/group_admins_page")
);

const groupBuilderShared = {
  app: BC_HQ,
  component: groupManagementPage,
  // These pages render their own heading through GroupManagementToolbarPortal,
  // which needs the bare toolbar as its portal host — the standard ContentToolBar
  // would nest a second titled, shadowed bar inside the first.
  contentToolbarComponent: GroupManagementContentToolbar,
  hideInNav: true,
  level: ["brand", "store", "group"] as ISecondaryNavProps["level"],
  scope: ["staff", "superuser"] as ISecondaryNavProps["scope"],
  dateSupport: DateRangeSupport.NONE,
  requiresFeatureFlag: LD_FEATURE_FLAGS.groupManagement,
};

const GROUP_MANAGEMENT: ISectionProps = {
  feature: "Group Management",
  icon: <ServersIcon />,
  app: BC_HQ,
  path: "/group-management",
  component: groupManagementPage,
  contentToolbarComponent: GroupManagementContentToolbar,
  subSections: [
    {
      ...groupBuilderShared,
      feature: "Create group",
      path: "/group-management/create",
    },
    {
      ...groupBuilderShared,
      feature: "Edit group",
      path: "/group-management/edit",
    },
    {
      ...groupBuilderShared,
      component: groupAdminsPage,
      feature: "Group admins",
      path: "/group-management/admins",
      // Visible in nav (unlike create/edit) so a super user can find it, and
      // ONLY to super users.
      hideInNav: false,
      requiresSuperAdmin: true,
    },
  ],
  level: ["brand", "store", "group"],
  dateSupport: DateRangeSupport.NONE,
  requiresFeatureFlag: LD_FEATURE_FLAGS.groupManagement,
};

export default GROUP_MANAGEMENT;
